import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ViewChildren,
  QueryList,
  ElementRef,
  OnDestroy,
  AfterViewInit,
} from '@angular/core';
import { Store } from '@ngrx/store';
import {
  StudioService,
  saveScene,
} from '../../element-editor/store/studio';
import { StudioLayoutService } from '../../element-editor/store/studio/layout';
import { StudioScene } from '../../element-editor/store/studio/studio.types';
import { LayoutCellDefinition } from '../../element-editor/store/studio/layout/layout-cell';
import { Application } from 'pixi.js';
import { StudioSceneShape } from '../../element-editor/shape/shapes/general/root/studio-scene-shape';
import { CanvasService } from '../../services/canvas/canvas.service';

interface CellEditorNode {
  label: string;
  shape?: string;
  type?: string;
  definition: LayoutCellDefinition;
  children: CellEditorNode[];
  expanded: boolean;
}

@Component({
  selector: 'nw-layout-options-modal',
  templateUrl: './layout-options-modal.component.html',
  styleUrls: ['./layout-options-modal.component.scss'],
})
export class LayoutOptionsModalComponent implements AfterViewInit, OnDestroy {
  @Output() close = new EventEmitter<void>();

  @ViewChild('previewCanvas') previewCanvasRef: ElementRef;
  @ViewChildren('optionCanvas') optionCanvases: QueryList<ElementRef>;

  optionScenes: StudioScene[] = [];
  selectedIndex = 0;
  editorTree: CellEditorNode | null = null;

  private previewApp: Application | null = null;
  private previewShape: StudioSceneShape | null = null;
  private optionApps: Application[] = [];
  private optionShapes: StudioSceneShape[] = [];

  private optionWidth = 160;
  private optionHeight = 90;

  get currentScene(): StudioScene | undefined {
    return this.studioService.currentSceneShape?.scene;
  }

  constructor(
    private layoutService: StudioLayoutService,
    private store: Store,
    private studioService: StudioService,
    private cs: CanvasService,
  ) {}

  ngAfterViewInit() {
    this.initOptions();
  }

  initOptions() {
    this.optionScenes = this.layoutService.getPossibleScenes();
    if (this.optionScenes.length > 0) {
      this.selectedIndex = 0;
      this.buildEditorTree(this.optionScenes[0]);
    }

    setTimeout(() => {
      this.renderOptionPreviews();
      this.renderMainPreview();
    }, 10);
  }

  private renderOptionPreviews() {
    this.cleanupOptionPreviews();
    const canvasElements = this.optionCanvases.toArray();
    const scale = this.optionWidth / this.cs.currentScreenX;

    for (let i = 0; i < this.optionScenes.length && i < canvasElements.length; i++) {
      const scene = this.optionScenes[i];
      const el = canvasElements[i];
      if (!el) continue;

      const app = new Application({
        width: this.optionWidth,
        height: this.optionHeight,
        backgroundAlpha: 1,
        backgroundColor: 0xf9fafb,
        antialias: true,
        autoDensity: true,
        resolution: window.devicePixelRatio || 1,
      });

      el.nativeElement.appendChild(app.view);
      this.optionApps.push(app);

      const shape = new StudioSceneShape(this.studioService, scene, {
        margin: 0,
        preview: true,
      });
      shape.container.position.set(0, 0);
      shape.bgContainer.position.set(0, 0);
      shape.container.scale.set(scale);
      shape.bgContainer.scale.set(scale);

      app.stage.addChild(shape.bgContainer);
      app.stage.addChild(shape.container);
      this.optionShapes.push(shape);
    }
  }

  private renderMainPreview() {
    this.cleanupMainPreview();
    const scene = this.optionScenes[this.selectedIndex];
    if (!scene || !this.previewCanvasRef) return;

    const el = this.previewCanvasRef.nativeElement;
    const containerWidth = el.parentElement?.clientWidth || 480;
    const previewWidth = containerWidth;
    const previewHeight = Math.round(previewWidth * (9 / 16));
    const scale = previewWidth / this.cs.currentScreenX;

    const app = new Application({
      width: previewWidth,
      height: previewHeight,
      backgroundAlpha: 1,
      backgroundColor: 0xf9fafb,
      antialias: true,
      autoDensity: true,
      resolution: window.devicePixelRatio || 1,
    });

    el.appendChild(app.view);
    this.previewApp = app;

    const shape = new StudioSceneShape(this.studioService, scene, {
      margin: 0,
      preview: true,
    });
    shape.container.position.set(0, 0);
    shape.bgContainer.position.set(0, 0);
    shape.container.scale.set(scale);
    shape.bgContainer.scale.set(scale);

    app.stage.addChild(shape.bgContainer);
    app.stage.addChild(shape.container);
    this.previewShape = shape;
  }

  selectOption(index: number) {
    this.selectedIndex = index;
    this.buildEditorTree(this.optionScenes[index]);
    this.renderMainPreview();
  }

  applyLayout() {
    const scene = this.optionScenes[this.selectedIndex];
    if (scene) {
      this.store.dispatch(saveScene({ value: scene }));
    }
    this.onClose();
  }

  onClose() {
    this.cleanupAll();
    this.close.emit();
  }

  regenerateOptions() {
    this.cleanupAll();
    this.initOptions();
  }

  // --- Cell Editor Tree ---

  private buildEditorTree(scene: StudioScene) {
    const def = scene.layoutDefinition;
    if (!def) {
      this.editorTree = null;
      return;
    }
    this.editorTree = this.buildNode(def, 'root');
  }

  private buildNode(def: LayoutCellDefinition, label: string): CellEditorNode {
    return {
      label: def.shape || label,
      shape: def.shape,
      type: def.type,
      definition: def,
      children: (def.children || []).map((child, i) =>
        this.buildNode(child, `child-${i}`),
      ),
      expanded: true,
    };
  }

  toggleNode(node: CellEditorNode) {
    node.expanded = !node.expanded;
  }

  updateNodeField(node: CellEditorNode, field: string, value: any) {
    (node.definition as any)[field] = value;
    this.rebuildSceneFromTree();
  }

  private rebuildSceneFromTree() {
    if (!this.editorTree) return;
    const scene = this.optionScenes[this.selectedIndex];
    if (!scene) return;

    const updatedScene: StudioScene = {
      ...scene,
      layoutDefinition: this.editorTree.definition,
    };
    this.optionScenes = [
      ...this.optionScenes.slice(0, this.selectedIndex),
      updatedScene,
      ...this.optionScenes.slice(this.selectedIndex + 1),
    ];
    this.renderMainPreview();
  }

  // --- Cleanup ---

  private cleanupOptionPreviews() {
    this.optionApps.forEach(app =>
      app.destroy(true, { children: true, texture: false, baseTexture: false }),
    );
    this.optionApps = [];
    this.optionShapes.forEach(s => s.remove());
    this.optionShapes = [];
  }

  private cleanupMainPreview() {
    if (this.previewApp) {
      this.previewApp.destroy(true, { children: true, texture: false, baseTexture: false });
      this.previewApp = null;
    }
    if (this.previewShape) {
      this.previewShape.remove();
      this.previewShape = null;
    }
  }

  private cleanupAll() {
    this.cleanupOptionPreviews();
    this.cleanupMainPreview();
  }

  ngOnDestroy() {
    this.cleanupAll();
  }
}
